import { useState, useEffect, useRef } from "react"
import { Popover, PopoverTrigger, PopoverContent } from "../../Components/UI/popover"
import { Button } from "../../Components/UI/button"
import {
  Command,
  CommandList,
  CommandItem,
  CommandEmpty
} from "../../Components/UI/command"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../Lib/Cn"
import { Label } from "../../Components/UI/label"

export function CustomDropdown({
  fieldID,
  required,
  label,
  placeholder,
  options,
  value,
  onChange,
  emptyMessage = ""   // ✅ default prop
}) {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const itemRefs = useRef([])

  const selectedOptionLabel =
    options.find((option) => option.value === value)?.label || placeholder

  // Reset focus index when dropdown opens
  useEffect(() => {
    if (open) {
      const selectedIndex = options.findIndex(opt => opt.value === value)
      setFocusedIndex(selectedIndex !== -1 ? selectedIndex : 0)
    } else {
      setFocusedIndex(-1)
    }
  }, [open, options, value])

  useEffect(() => {
    if (focusedIndex !== -1 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].focus()
    }
  }, [focusedIndex])

  const handleKeyDown = (e) => {
    if (!open) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % options.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusedIndex((prev) => (prev - 1 + options.length) % options.length)
    } else if (e.key === "Enter" && focusedIndex !== -1) {
      e.preventDefault()
      const selectedOption = options[focusedIndex]
      onChange(selectedOption.value)
      setOpen(false)
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  return (
    <div key={fieldID} className={`space-y-1.5`}>
      <Label htmlFor={fieldID} required={required}>
        {label}
      </Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            onKeyDown={handleKeyDown}
            className={cn(
              "w-full flex justify-between items-center font-normal h-11 text-sm text-left overflow-hidden p-4 sahi-input",
              "border-input bg-white shadow-xs transition-[color,box-shadow] outline-none",
              "hover:border-gray-400 cursor-pointer",
              "focus:border-sahi-primary-color focus:ring-1 focus:ring-sahi-primary-color",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            {selectedOptionLabel}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="p-1 bg-[var(--sahi-white)] border-none shadow-lg"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <Command>
            <CommandList className="max-h-[200px] overflow-y-auto">
              {options.length === 0 ? (
                  <CommandEmpty>
                  {emptyMessage || (
                    <>
                      No {label.toLowerCase()} found.
                    </>
                  )}
                </CommandEmpty>
              ) : (
                options.map((option, index) => (
                  <CommandItem
                    key={`${option.value}-${index}`}
                    value={`${option.label} ${option.value} ${index}`}
                    ref={(el) => (itemRefs.current[index] = el)}
                    tabIndex={-1}
                    onSelect={() => {
                      onChange(option.value)
                      setOpen(false)
                    }}
                    className={cn(
                      index === focusedIndex && "bg-sahi-light-grey text-sahi-primary-color",
                      value === option.value && "font-semibold",
                      "cursor-pointer"
                    )}
                  >
                    {option.label}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
